import {
  getStoredSiderWidth,
  MAX_SIDER_WIDTH,
  MIN_SIDER_WIDTH,
  SIDER_WIDTH_STORAGE_KEY,
} from '@zhiyue/core';
import React from 'react';

export {
  getStoredSiderWidth,
  MAX_SIDER_WIDTH,
  MIN_SIDER_WIDTH,
  SIDER_WIDTH_STORAGE_KEY,
};

export const DEFAULT_SIDER_WIDTH = 190;
const COLLAPSE_THRESHOLD = 120;

type Props = {
  enabled: boolean;
  siderWidth: number;
  onResizeWidth: (width: number) => void;
  onCollapse: () => void;
};

const clamp = (width: number) =>
  Math.min(MAX_SIDER_WIDTH, Math.max(MIN_SIDER_WIDTH, width));

const SiderResizeHandle: React.FC<Props> = ({
  enabled,
  siderWidth,
  onResizeWidth,
  onCollapse,
}) => {
  const [edge, setEdge] = React.useState<number | null>(null);
  const [dragging, setDragging] = React.useState(false);

  React.useEffect(() => {
    if (!enabled) return;
    const sider = document.querySelector('.ant-pro-sider');
    if (!sider) return;
    const update = () => setEdge(sider.getBoundingClientRect().right);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(sider);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [enabled, siderWidth]);

  if (!enabled || edge === null) return null;

  return (
    <div
      className={`task-sider-resize${dragging ? ' task-sider-resize-dragging' : ''}`}
      style={{ left: edge - 6 }}
      role="slider"
      tabIndex={0}
      aria-label="调整侧边栏宽度"
      aria-orientation="vertical"
      aria-valuemin={56}
      aria-valuemax={MAX_SIDER_WIDTH}
      aria-valuenow={siderWidth}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        const startX = event.clientX;
        const startWidth = siderWidth;
        setDragging(true);
        let finalWidth = startWidth;
        const onMove = (moveEvent: PointerEvent) => {
          finalWidth = startWidth + moveEvent.clientX - startX;
          if (finalWidth >= COLLAPSE_THRESHOLD) {
            const width = clamp(finalWidth);
            try {
              window.localStorage.setItem(
                SIDER_WIDTH_STORAGE_KEY,
                String(width),
              );
            } catch {
              /* storage unavailable */
            }
            onResizeWidth(width);
          }
        };
        const onEnd = () => {
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onEnd);
          window.removeEventListener('pointercancel', onEnd);
          document.body.style.cursor = '';
          document.body.style.userSelect = '';
          setDragging(false);
          if (finalWidth < COLLAPSE_THRESHOLD) onCollapse();
        };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onEnd);
        window.addEventListener('pointercancel', onEnd);
      }}
      onKeyDown={(event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const next = siderWidth + (event.key === 'ArrowLeft' ? -24 : 24);
        if (next < MIN_SIDER_WIDTH) {
          onCollapse();
        } else {
          const width = clamp(next);
          try {
            window.localStorage.setItem(SIDER_WIDTH_STORAGE_KEY, String(width));
          } catch {
            /* storage unavailable */
          }
          onResizeWidth(width);
        }
      }}
    >
      <span className="task-sider-resize-line" />
    </div>
  );
};

export default SiderResizeHandle;
